import { extent } from 'd3-array';
import { isNumber } from 'lodash-es';

import type { RangeItem } from '../types/range-legend.js';

const excludedValues = [-Infinity, Infinity, NaN];

/**
 * [
 *   \{from: -Infinity, to: 'some-value'\},
 *   \{from: 'some-value', to: Infinity\}
 * ]
 */
const SINGLE_SCALED_INFINITY_RANGE_LENGTH = 3;

const sanitizeValues = (values: number[]) => {
  if (values.length > SINGLE_SCALED_INFINITY_RANGE_LENGTH) {
    return values;
  }

  return values.map((value, index, self) => {
    switch (value) {
      case -Infinity:
      case Infinity: {
        if (index === 0) {
          return self[index + 1] - 1;
        }

        return self[index - 1] + 1;
      }
      default:
        return value;
    }
  });
};

const searchBiggestRangeGap = (values: number[]) =>
  values
    .filter((value) => !excludedValues.includes(value))
    .reduce((maxStepDiff, value, index, self) => {
      const stepDiff = value - (self[index - 1] ?? value);

      return stepDiff > maxStepDiff ? stepDiff : maxStepDiff;
    }, 0);

const findMinMax = (values: number[]) => {
  if (values[0] === -Infinity && values[1] === Infinity) {
    return { min: 0, max: 1 };
  }

  const sanitizedValues = sanitizeValues(values);

  let [min = 0, max = 0] = extent(sanitizedValues);

  if (min === max) {
    max = min + 1;
  }

  if (min === -Infinity || max === Infinity) {
    const maxRangeGap = searchBiggestRangeGap(sanitizedValues);

    const sanitizedRanges = sanitizedValues.filter(
      (v) => !excludedValues.includes(v),
    );

    const [sanitizedMin = 0, sanitizedMax = 0] = extent(sanitizedRanges);

    min = min === -Infinity ? sanitizedMin - maxRangeGap : min;
    max = max === Infinity ? sanitizedMax + maxRangeGap : max;
  }

  return { min, max };
};

export const getMinMaxValues = (
  items: RangeItem[],
  min?: number,
  max?: number,
) => {
  const isValidMin = isNumber(min) && isFinite(min);
  const isValidMax = isNumber(max) && isFinite(max);

  if (isValidMin && isValidMax) {
    // oxlint-disable-next-line typescript/no-non-null-assertion
    return { min: min!, max: max! };
  }

  const flatValues = items.flatMap((item) => [item.from, item.to]);

  const filteredValues = [...new Set(flatValues)];

  const sortedValues = filteredValues.sort((a, b) => a - b);

  if (min === -Infinity) {
    sortedValues.unshift(min);
  }

  if (max === Infinity) {
    sortedValues.push(max);
  }

  const { max: finalMax, min: finalMin } = findMinMax(sortedValues);

  return {
    // oxlint-disable-next-line typescript/no-non-null-assertion
    max: isValidMax ? max! : finalMax,
    // oxlint-disable-next-line typescript/no-non-null-assertion
    min: isValidMin ? min! : finalMin,
  };
};
